import { useEffect, useRef, useState } from "react"
import { Link } from "react-router"
import { accountAnalyticsExportUrl, fetchAccountAnalytics } from "../lib/api"
import useUser from "../hooks/useUser"
import useUpgrade from "../hooks/useUpgrade"
import {
  Icon, fmt, axisLabels, countryRows, deviceRows, BotNote,
  ChartCard, AnStat, AreaChart, BarRows, Hours, LockedPanel,
} from "./analytics/charts"

// Account-wide analytics at /app/analytics — the portfolio rollup across all
// of a user's codes. Shape from `Analytics::AccountReport`.
//
// Paywall (see `docs/specs/2026-09-12-analytics-entitlement-enforcement.md`):
// lifetime totals, code counts and the leaderboard stay sharp for everyone —
// they're sums of per-code totals, which free always sees. Everything that
// needs per-scan detail gates together once any code in the payload's scope is
// past its 50-scan window: scans-this-period and its trend, unique visitors,
// the over-time chart, and the three audience breakdowns.
//
// The server omits those rather than returning them for the frontend to blur,
// so every read below is guarded — `data.charts` and the locked `totals` keys
// are absent, not zero. A zero here would be a lie rather than a lock.

const RANGES = [
  ["7d",  "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
]

const PERIOD_WORD = { "7d": "this week", "30d": "this month", "90d": "this quarter" }

// Groups (docs/plans/2026-09-15-code-groups.plan.md): a group filter re-scopes
// the whole report, and the gate is evaluated over exactly the filtered codes —
// so a free account locked as a whole can see full detail for a group whose
// codes are all inside their windows. The by-group block's range figures are
// gated per row by the same rule; a row without them shows a lock, never a 0.
const RANGE_DAYS  = { "7d": 7, "30d": 30, "90d": 90 }

export default function AccountAnalyticsPage() {
  const { user } = useUser()
  const isPro = !!user?.entitlements?.paid
  const canExport = !!user?.features?.analytics_export

  const [status, setStatus] = useState("loading") // 'loading' | 'ready' | 'error'
  const [data, setData] = useState(null)
  const [range, setRange] = useState("30d")
  const [group, setGroup] = useState("")
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  const { upgrade } = useUpgrade()

  useEffect(() => {
    let cancelled = false
    fetchAccountAnalytics(range, group)
      .then((payload) => {
        if (cancelled) return
        setData(payload)
        setError(null)
        setStatus("ready")
      })
      .catch((e) => {
        if (cancelled) return
        setError(e)
        // Every figure on this page is scoped to a range and a group, so stale
        // data under a changed control isn't "still useful" — it's another
        // scope's numbers labelled with this one's. A group deleted in another
        // tab 404s here; showing the previous group's totals under its name is
        // worse than showing nothing.
        setStatus("error")
      })
    return () => { cancelled = true }
  }, [range, group, reloadKey])

  if (status === "loading") {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4 text-center text-[color:var(--color-muted)] dark:text-muted-light text-sm">
        Loading analytics…
      </div>
    )
  }

  if (status === "error") {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="border border-fail rounded p-4 text-fail text-sm">
          {error?.message || "Couldn't load your analytics."}
        </div>
        {/* The group filter isn't rendered in this state, so without a way back
            to all codes a deleted group would strand the page until a reload. */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            className="bg-voltage text-coal rounded-lg px-3.5 py-1.5 font-semibold hover:bg-voltage-dark transition"
          >
            Try again
          </button>
          {group && (
            <button type="button" onClick={() => setGroup("")} className="text-voltage-dark dark:text-voltage-light hover:underline">
              Show all codes
            </button>
          )}
          <Link to="/app" className="text-voltage hover:underline">
            ← Back to your codes
          </Link>
        </div>
      </div>
    )
  }

  const t = data.totals
  const days = RANGE_DAYS[data.range] || 30
  const charts = data.charts
  const series = charts?.scans_over_time ?? []
  // Keyed off what arrived, not off the entitlement. With AnalyticsGate off the
  // payload is complete and everything renders; with it on the detail is absent
  // and this page shows the locked composition. That makes the flag a real
  // rollback for the user-visible reduction rather than a wire-only switch —
  // see docs/specs/2026-09-12-analytics-entitlement-enforcement.md.
  const detailLocked = !charts
  const hasScans = t.lifetime_scans > 0
  const groups = data.groups || []
  const codesPhrase = `${t.code_count} ${t.code_count === 1 ? "code" : "codes"}`
  // Locked as a whole, but at least one group would show full detail: say so,
  // so the lock reads as a rule the user can work with rather than a wall.
  const unlockedGroupExists = detailLocked && groups.some((g) => "scans_in_range" in g)

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      <div className="flex flex-col gap-4">
        {/* Section header + range switch */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-ink dark:text-cloud leading-none">Analytics</h1>
            <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-2">
              {data.group
                ? <>Every scan across the {codesPhrase} in <span className="font-semibold text-ink dark:text-cloud">{data.group.name}</span>.{" "}</>
                : <>Every scan across your {codesPhrase}.{" "}</>}
              {isPro ? (
                <span className="inline-flex items-center gap-1 text-plasma dark:text-plasma-light">
                  <Icon name="sparkles" size={13} /> Full audience data unlocked.
                </span>
              ) : (
                "Your totals are always free."
              )}
            </p>
          </div>
          {/* Hidden when locked: every range-dependent figure is withheld, so
              the control would refetch and change nothing visible — the same
              dead-control problem useUpgrade was written to fix. */}
          <div className="flex flex-wrap items-center gap-2 self-start">
            {groups.length > 0 && <GroupSelect value={group} groups={groups} onChange={setGroup} />}
            {!detailLocked && <RangeSwitch range={range} onChange={setRange} />}
            {canExport && <ExportMenu range={range} group={group} />}
          </div>
        </div>

        {hasScans ? null : (
          <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-sm p-4 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
            No scans yet — share a code to start seeing analytics.
          </div>
        )}

        {/* Headline stat strip. Locked drops to the two tiles that carry real
            numbers on every plan — lifetime total and active codes — rather
            than dashing out the two that don't. */}
        {detailLocked ? (
          <div className="grid grid-cols-1 min-[860px]:grid-cols-2 gap-3">
            <AnStat
              emphasis
              eyebrow="Total scans"
              value={fmt(t.lifetime_scans)}
              sub={`across ${t.code_count} ${t.code_count === 1 ? "code" : "codes"}`}
            />
            <AnStat eyebrow="Active codes" value={fmt(t.active_code_count)} sub={`of ${t.code_count} total`} />
          </div>
        ) : (
          <div className="grid grid-cols-1 min-[860px]:grid-cols-2 min-[920px]:grid-cols-4 gap-3">
            <AnStat
              emphasis
              eyebrow={`Scans ${PERIOD_WORD[data.range]}`}
              value={fmt(t.scans_in_range)}
              trend={t.trend_pct}
              spark={series.map((p) => p.count)}
              sub="vs prior period"
            />
            <AnStat eyebrow="Total scans" value={fmt(t.lifetime_scans)} sub={`across ${t.code_count} ${t.code_count === 1 ? "code" : "codes"}`} />
            <AnStat eyebrow="Unique visitors" value={fmt(t.unique_visitors)} sub="est. distinct devices" />
            <AnStat eyebrow="Active codes" value={fmt(t.active_code_count)} sub={`of ${t.code_count} total`} />
          </div>
        )}

        {/* Scans over time — gated with the rest of the detail. When locked it
            isn't replaced in place; the single LockedPanel below the
            leaderboard speaks for everything that gates together. */}
        {!detailLocked && (
          <ChartCard
            title={`Scans over time · last ${days} days`}
            right={<span className="font-mono text-sm text-ink dark:text-cloud">{fmt(t.scans_in_range)}</span>}
          >
            <AreaChart series={series.map((p) => p.count)} labels={axisLabels(series)} height={170} />
          </ChartCard>
        )}

        {/* Per-group totals — the navigation for the group filter. */}
        {groups.length > 0 && (
          <GroupsCard rows={groups} active={group} onSelect={setGroup} periodWord={PERIOD_WORD[data.range]} />
        )}

        {/* Top performing codes — sharp; the codes list ranked by scans */}
        <Leaderboard rows={data.leaderboard} lifetime={t.lifetime_scans} />

        {detailLocked ? (
          <LockedPanel
            lead={
              `We've recorded all ${fmt(t.lifetime_scans)} scans across ` +
              (data.group ? `the ${codesPhrase} in ${data.group.name}.` : `your ${codesPhrase}.`)
            }
            items={[
              "Daily scans and trend",
              "Countries",
              "Devices",
              "Time of day",
              "Unique visitors",
              "Every code past its free window",
            ]}
            onUpgrade={upgrade}
          />
        ) : null}
        {detailLocked && unlockedGroupExists ? (
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light -mt-1">
            Some of your groups have every code inside its free window. Pick one of them above to see its full detail.
          </p>
        ) : null}
        {detailLocked ? null : (
          <>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">Audience</span>
            </div>
            {charts && (
              <div className="grid grid-cols-1 min-[860px]:grid-cols-3 gap-3">
                <ChartCard title="By country">
                  <BarRows rows={countryRows(charts.by_country, { limit: 6 })} />
                </ChartCard>
                <ChartCard title="By device">
                  <BarRows rows={deviceRows(charts.by_device)} />
                  <BotNote rows={charts.by_device} />
                </ChartCard>
                <ChartCard title="Time of day · UTC">
                  <Hours buckets={charts.by_hour} />
                </ChartCard>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ---- Group filter + per-group totals -----------------------------------

function GroupSelect({ value, groups, onChange }) {
  return (
    <>
      <label className="sr-only" htmlFor="analytics-group">Group</label>
      <select
        id="analytics-group"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="max-w-[200px] rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight"
      >
        <option value="">All codes</option>
        {groups.map((g) => <option key={String(g.id)} value={String(g.id)}>{g.name}</option>)}
      </select>
    </>
  )
}

// One row per group (and Ungrouped), across the whole account whatever the
// filter. Lifetime totals always show; the period figures and trend show only
// when that group's own codes are inside their windows — otherwise a lock,
// never a zero. A row is a button that applies the filter.
function GroupsCard({ rows, active, onSelect, periodWord }) {
  return (
    <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">By group</span>
        {active && (
          <button type="button" onClick={() => onSelect("")} className="text-xs text-voltage-dark dark:text-voltage-light hover:underline">
            Show all codes
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-xs text-[color:var(--color-muted)] dark:text-muted-light">
              <th className="font-medium py-1.5 pr-3">Group</th>
              <th className="font-medium py-1.5 px-3 text-right">Codes</th>
              <th className="font-medium py-1.5 px-3 text-right">Scans {periodWord}</th>
              <th className="font-medium py-1.5 px-3 text-right">Trend</th>
              <th className="font-medium py-1.5 pl-3 text-right">All time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const id = String(r.id)
              const selected = id === String(active)
              const hasDetail = "scans_in_range" in r
              return (
                <tr key={id} className={"border-t border-[color:var(--color-border)] dark:border-storm " + (selected ? "bg-voltage-pale/60 dark:bg-voltage/10" : "")}>
                  <td className="py-2 pr-3 max-w-[240px]">
                    <button type="button" onClick={() => onSelect(selected ? "" : id)} aria-pressed={selected}
                      className={"truncate text-left hover:text-voltage-dark dark:hover:text-voltage-light " + (r.id === "ungrouped" ? "italic text-[color:var(--color-muted)] dark:text-muted-light" : "text-ink dark:text-cloud")}>
                      {r.name}
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(r.code_count)}</td>
                  <td className="py-2 px-3 text-right font-mono">{hasDetail ? fmt(r.scans_in_range) : <RowLock />}</td>
                  <td className="py-2 px-3 text-right font-mono">{hasDetail ? <Trend pct={r.trend_pct} /> : <RowLock />}</td>
                  <td className="py-2 pl-3 text-right font-mono">{fmt(r.lifetime_scans)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function RowLock() {
  return (
    <span className="inline-flex text-[color:var(--color-muted)] dark:text-muted-light" title="A code in this group is past its free 50 scans" aria-label="Locked">
      <Icon name="lock" size={13} />
    </span>
  )
}

function Trend({ pct }) {
  const up = pct >= 0
  return <span className={up ? "text-voltage-dark dark:text-voltage-light" : "text-fail"}>{up ? "+" : ""}{pct}%</span>
}

// ---- CSV export ---------------------------------------------------------
//
// Three shapes of the page's own scope (range + group). Plain download links —
// the server builds the file and gates each row exactly as this page is gated,
// so a locked code's windowed count is blank and it has no day or scan rows.
// Every date in the file is UTC; the page shows local.

const EXPORTS = [
  ["codes", "Per code", "Totals for each code"],
  ["days",  "Per day",  "Scans per code per day"],
  ["scans", "Per scan", "Time, country, device"],
]

function ExportMenu({ range, group }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === "Escape") setOpen(false) }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight px-3 py-2 text-sm font-medium text-ink dark:text-cloud hover:border-voltage focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 dark:focus:ring-offset-midnight"
      >
        <Icon name="download" size={14} /> Export CSV
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-[color:var(--color-border)] dark:border-storm bg-white dark:bg-midnight shadow-lg p-1">
          {EXPORTS.map(([rows, label, hint]) => (
            <a
              key={rows}
              href={accountAnalyticsExportUrl(rows, range, group)}
              download
              onClick={() => setOpen(false)}
              className="flex flex-col rounded-md px-3 py-2 hover:bg-[color:var(--color-cloud)] dark:hover:bg-coal focus:outline-none focus:ring-2 focus:ring-voltage"
            >
              <span className="text-sm text-ink dark:text-cloud">{label}</span>
              <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">{hint}</span>
            </a>
          ))}
          <p className="px-3 pt-1 pb-2 text-xs text-[color:var(--color-muted)] dark:text-muted-light">Dates in UTC.</p>
        </div>
      )}
    </div>
  )
}

// ---- Range switch (segmented control) ---------------------------------

function RangeSwitch({ range, onChange }) {
  return (
    <div
      role="group"
      aria-label="Time range"
      className="inline-flex self-start rounded-lg border border-[color:var(--color-border)] dark:border-storm p-0.5 bg-white dark:bg-midnight"
    >
      {RANGES.map(([id, label]) => {
        const active = range === id
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={active}
            className={
              "px-3 py-1.5 text-sm font-medium rounded-md transition " +
              (active
                ? "bg-voltage text-coal"
                : "text-[color:var(--color-muted)] dark:text-muted-light hover:text-ink dark:hover:text-cloud")
            }
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

// ---- Top performing codes leaderboard ---------------------------------
//
// Up to 5 rows ranked by lifetime scans; the whole row links to the per-code
// page. Share bar is relative to the top scorer; the share % is of the
// account lifetime total. Always sharp — it's the codes list a free user can
// already see, just ranked.

function Leaderboard({ rows, lifetime }) {
  return (
    <div className="bg-white dark:bg-midnight border border-[color:var(--color-border)] dark:border-storm rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">Top performing codes</span>
        <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">by scans · all time</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs italic text-[color:var(--color-muted)] dark:text-muted-light py-2">No scans yet</div>
      ) : (
        <div className="flex flex-col">
          {rows.map((c, i) => (
            <LeaderboardRow key={c.token} code={c} rank={i + 1} peak={rows[0].scans} lifetime={lifetime} />
          ))}
        </div>
      )}
    </div>
  )
}

function LeaderboardRow({ code, rank, peak, lifetime }) {
  const shortLabel = (code.short_url || `tgo.sh/${code.token}`).replace(/^https?:\/\//, "")
  const share = lifetime > 0 ? Math.round((code.scans / lifetime) * 100) : 0
  return (
    <Link
      to={`/app/codes/${code.token}`}
      className="group flex items-center gap-3 py-2.5 -mx-2 px-2 rounded-md hover:bg-[color:var(--color-cloud)] dark:hover:bg-coal transition"
    >
      <span className="w-5 text-center font-mono text-sm text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0">{rank}</span>
      <img
        src={code.image_url}
        alt=""
        aria-hidden="true"
        className="w-9 h-9 rounded border border-[color:var(--color-border)] dark:border-storm bg-white flex-shrink-0"
      />
      <span className="flex flex-col min-w-0 flex-1">
        <span className="text-sm text-ink dark:text-cloud truncate">{code.label}</span>
        <span className="font-mono text-xs text-voltage truncate">{shortLabel}</span>
      </span>
      <span className="hidden min-[680px]:block w-[130px] h-1.5 bg-[color:var(--color-cloud)] dark:bg-coal rounded-full overflow-hidden flex-shrink-0">
        <span
          className="block h-full bg-voltage rounded-full"
          style={{ width: `${Math.max(4, (code.scans / Math.max(1, peak)) * 100)}%` }}
        />
      </span>
      <span className="flex flex-col items-end w-16 flex-shrink-0">
        <span className="font-mono text-sm text-ink dark:text-cloud">{fmt(code.scans)}</span>
        <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">{share}%</span>
      </span>
      <span className="text-[color:var(--color-muted)] dark:text-muted-light group-hover:text-voltage transition flex-shrink-0">
        <Icon name="arrowRight" size={15} />
      </span>
    </Link>
  )
}
